import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateDealViews } from "@/lib/queryKeys";
import { DealInfo, PipelineStage } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { operationalUiErrorMessage } from "@/lib/operationalUiError";

interface UseDealsOptions {
  companyId?: number;
  mandanteCompanyId?: number;
  contactId?: number;
  status?: string;
  forContactDetail?: boolean;
  forCompanyDetail?: boolean;
}

export const useDeals = (options?: UseDealsOptions) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId, mandanteCompanyId, contactId, status, forContactDetail, forCompanyDetail } = options || {};

  // Determine the API URL based on options
  let apiUrl = '/api/deals';
  
  if (forContactDetail && contactId) {
    // Use the specific endpoint for contact's deals
    apiUrl = `/api/contacts/${contactId}/deals`;
  } else if (forCompanyDetail && companyId) {
    // Use the specific endpoint for company's deals
    apiUrl = `/api/companies/${companyId}/deals`;
  } else {
    // Regular deals endpoint with query parameters
    const queryParams = new URLSearchParams();
    if (companyId) queryParams.append('companyId', companyId.toString());
    if (mandanteCompanyId) queryParams.append('mandanteCompanyId', mandanteCompanyId.toString());
    if (contactId) queryParams.append('contactId', contactId.toString());
    if (status) queryParams.append('status', status);
    
    apiUrl = `/api/deals${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  }

  const {
    data: deals,
    isLoading,
    isError,
    error,
  } = useQuery<DealInfo[]>({
    queryKey: [apiUrl],
  });

  const createDeal = useMutation({
    mutationFn: async (dealData: Omit<DealInfo, 'id' | 'createdAt' | 'updatedAt'> & { source?: string }) => {
      // BUSINESS RULE: I deal NON si creano MAI automaticamente
      const enrichedData = {
        ...dealData,
        createdBy: 'user',
        source: dealData.source || 'manual',
      };

      // ───────────────────────────────────────────────────────────────────
      // FIX #3a — BUG: firma apiRequest() sbagliata → create/update/delete rotti
      // Autore: Claude Code (25 giugno 2026) — richiesto da Michele
      //
      // PROBLEMA:
      //   apiRequest() in queryClient.ts ha firma: (endpoint: string, options: RequestInit)
      //   Qui veniva chiamata come: apiRequest("POST", "/api/deals", data)
      //   Risultato: endpoint="/POST", options="/api/deals" (stringa, non oggetto),
      //   body ignorato → la chiamata faceva GET verso /POST → 404 garantito.
      //
      // SOLUZIONE:
      //   Usare la firma corretta passando method e body dentro options.
      // ───────────────────────────────────────────────────────────────────
      return await apiRequest('/api/deals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(enrichedData),
      });
    },
    onSuccess: (data) => {
      toast({
        title: "Success",
        description: "Deal created successfully",
      });
      invalidateDealViews(queryClient, data?.id);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: operationalUiErrorMessage(error, "L'opportunità non è stata creata. Riprova tra poco."),
        variant: "destructive",
      });
    },
  });

  const updateDeal = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<DealInfo> }) => {
      return await apiRequest(`/api/deals/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
    },
    onSuccess: (res, variables) => {
      // Aggiorna immediatamente la cache per evitare flash/refresh
      const updatedDeal = res.json ? res.json() : res;
      
      // Invalida e refetch le query deal
      invalidateDealViews(queryClient, variables.id);
      
      // Toast solo se non è drag & drop kanban
      if (!variables.data.hasOwnProperty('stageId')) {
        toast({
          title: "Success",
          description: "Deal updated successfully",
        });
      }
    },
    onError: (error) => {
      console.error("Deal update error:", error);
      toast({
        title: "Error",
        description: operationalUiErrorMessage(error, "L'opportunità non è stata aggiornata. Riprova tra poco."),
        variant: "destructive",
      });
    },
  });

  const deleteDeal = useMutation({
    mutationFn: async (id: number) => {
      // FIX #3c — stessa firma sbagliata (vedi commento in createDeal sopra)
      return await apiRequest(`/api/deals/${id}`, { method: 'DELETE' });
    },
    onSuccess: (_, deletedId) => {
      toast({
        title: "Success",
        description: "Deal deleted successfully",
      });
      
      invalidateDealViews(queryClient, deletedId);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: operationalUiErrorMessage(error, "L'opportunità non è stata eliminata. Riprova tra poco."),
        variant: "destructive",
      });
    },
  });

  return {
    deals,
    isLoading,
    isError,
    error,
    createDeal,
    updateDeal,
    deleteDeal,
  };
};

export const useDeal = (id: number) => {
  const { data, isLoading, isError, error, refetch } = useQuery<DealInfo>({
    queryKey: [`/api/deals/${id}`],
    enabled: !!id,
  });
  
  return { data, isLoading, isError, error, refetch };
};

export const usePipelineStages = () => {
  return useQuery<PipelineStage[]>({
    queryKey: ['/api/pipeline-stages'],
  });
};
